import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import StarRating from '../../library/star-rating/star-rating';
import Tooltip from '../../library/tooltip/tooltip';
import Message from '../../components/message/message';
import GetItFastCategory from './components/get-it-fast-category/get-it-fast-category';
import OptionCategory from './components/option-category/option-category';
import PriceRangeCategory from './components/price-range-category/price-range-category';
import LazyLoad from '../lazy-load/lazy-load';

import { useAemContent } from '../../shared/hooks/hooks';

import {
  getFilterId,
  getQueryParametersFromUrl
} from '../../shared/utils/filters/filters';
import { getAccessoryCategoryType } from '../../shared/utils/product/product';
import { ProductListFiltersProp } from '../../shared/utils/prop-types/prop-types';

import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../shared/constants/cms';
import { FILTER_CATEGORY_CODES } from '../../shared/constants/filters';

import './product-list-filter.scss';

function ProductListFilter(props) {
  const {
    accessoryCategory,
    defaultMaxPrice,
    defaultMinPrice,
    filters = [],
    highestPriceInResults,
    isAccessorySearch,
    isChangeStoreDisabled,
    isLoadingPLP,
    isPrebuiltPackageSearch,
    isTextSearch,
    lowestPriceInResults,
    onSelectFilter: onSelectFilterProp,
    onSliderValueChange,
    query: queryProp,
    selectedFilters = [],
    shouldApplySliderOnChange = false,
    toggleType
  } = props;

  const [openShowMoreButtonIds, setOpenShowMoreButtonIds] = useState([]);
  const [lastSelectedFilter, setLastSelectedFilter] = useState();
  const [showToastMsg, setShowToastMsg] = useState(false);
  const location = useLocation();

  const { aemTooltipsContent: aemTooltipsContentMultiStore } = useAemContent(
    AEM_CONTENT_PAGE_IDS.global
  );

  const changeStoreToastMsg =
    aemTooltipsContentMultiStore?.[AEM_CONTENT_IDS.plpMultiStoreToastMsg]
      ?.tipText;

  const { query: queryFromUrl } = getQueryParametersFromUrl(
    queryString.parse(location.search)
  );
  const query = queryProp || queryFromUrl;

  const hasSelectedPriceRangeFilter = Boolean(
    Array.isArray(selectedFilters) &&
      selectedFilters.find(
        selectedFilter =>
          selectedFilter.categoryCode === FILTER_CATEGORY_CODES.PRICE_FILTERS ||
          selectedFilter.categoryCode ===
            FILTER_CATEGORY_CODES.BUNDLE_PRICE_FILTERS
      )
  );

  function onSelectFilter(filterToToggle) {
    onSelectFilterProp?.(filterToToggle);
    setLastSelectedFilter(filterToToggle);
  }

  function handleToggleShowMore(buttonId) {
    setOpenShowMoreButtonIds(prevIds =>
      prevIds.includes(buttonId)
        ? prevIds.filter(id => id !== buttonId)
        : [...prevIds, buttonId]
    );
  }

  useEffect(() => {
    if (!isLoadingPLP && lastSelectedFilter) {
      const filterId = getFilterId(lastSelectedFilter);
      const el = document.getElementById(filterId);

      if (el) {
        el.focus();
        setLastSelectedFilter();
      }
    }
  }, [isLoadingPLP, lastSelectedFilter, setLastSelectedFilter]);

  useEffect(() => {
    if (showToastMsg) {
      setTimeout(() => setShowToastMsg(false), 5000);
    }
  }, [showToastMsg]);

  if (isLoadingPLP) {
    return null;
  }

  return (
    <>
      {showToastMsg && <Message isSuccess message={changeStoreToastMsg} />}
      {filters?.map(filter => {
        const filteredFilter = { ...filter };

        if (
          !isTextSearch &&
          filter.categoryCode === FILTER_CATEGORY_CODES.QUICK_FILTERS
        ) {
          filteredFilter.values = filter.values.filter(
            v => v.categoryCode !== 'accessories-boolean'
          );
        }

        if (
          (isAccessorySearch &&
            !getAccessoryCategoryType(accessoryCategory) &&
            filteredFilter?.categoryCode === 'accessoryRootCategory') ||
          (isAccessorySearch &&
            getAccessoryCategoryType(accessoryCategory) &&
            filteredFilter?.categoryCode === 'accessoryCategory') ||
          (isPrebuiltPackageSearch &&
            filteredFilter?.categoryCode === 'allVehiclesRatingRange')
        ) {
          return;
        }

        const commonProps = {
          categoryName: filteredFilter?.categoryName,
          isOpenByDefault: filteredFilter?.isOpenByDefault
        };

        let category = null;

        if (
          filteredFilter.categoryCode ===
          FILTER_CATEGORY_CODES.AVAILABILITY_FILTERS
        ) {
          category = (
            <GetItFastCategory
              filterValues={filteredFilter.values}
              isChangeStoreDisabled={isChangeStoreDisabled}
              key={`${filteredFilter.categoryCode}_${query}`}
              onClick={onSelectFilter}
              onSaveClick={setShowToastMsg}
              selectedFilters={selectedFilters}
              toggleType={toggleType}
            />
          );
        } else if (
          filteredFilter.categoryCode === FILTER_CATEGORY_CODES.PRICE_FILTERS ||
          filteredFilter.categoryCode ===
            FILTER_CATEGORY_CODES.BUNDLE_PRICE_FILTERS
        ) {
          category = (
            <LazyLoad
              key={`${filteredFilter.categoryCode}_${query}`}
              placeholder={<div styleName="category-placeholder" />}
            >
              <PriceRangeCategory
                categoryCode={filteredFilter.categoryCode}
                defaultMaxPrice={defaultMaxPrice}
                defaultMinPrice={defaultMinPrice}
                description={filteredFilter.description}
                highestPriceInResults={highestPriceInResults}
                isSelected={hasSelectedPriceRangeFilter}
                key={`${filteredFilter.categoryCode}${
                  defaultMinPrice ? `_${defaultMinPrice}` : ''
                }${defaultMaxPrice ? `_${defaultMaxPrice}` : ''}`}
                lowestPriceInResults={lowestPriceInResults}
                onApply={onSelectFilter}
                onSliderValueChange={onSliderValueChange}
                shouldApplySliderOnChange={shouldApplySliderOnChange}
                toggleType={toggleType}
                {...commonProps}
              />
            </LazyLoad>
          );
        } else {
          const buttonId = getFilterId({
            categoryCode: filteredFilter.categoryCode,
            type: 'button'
          });

          const showMore = openShowMoreButtonIds.includes(buttonId);

          const getShowMoreByDefault = () => {
            if (showMore) {
              return true;
            }
            if (filteredFilter.categoryName.includes('Brands')) {
              return false;
            }
            return filteredFilter.showMoreByDefault;
          };
          category = (
            <LazyLoad
              key={`${filteredFilter.categoryCode}_${query}`}
              placeholder={<div styleName="category-placeholder" />}
            >
              <OptionCategory
                buttonId={buttonId}
                description={filteredFilter.description}
                filterValues={filteredFilter.values}
                hasTextFiltering={filteredFilter.categoryName.includes(
                  'Brands'
                )}
                key={`${filteredFilter.categoryCode}_${query}`}
                onClick={onSelectFilter}
                onToggleShowMore={handleToggleShowMore}
                selectedFilters={selectedFilters}
                showMoreByDefault={getShowMoreByDefault()}
                textFilteringPlaceholder={
                  filteredFilter.categoryName.includes('Brands')
                    ? 'Search Brand...'
                    : ''
                }
                toggleType={toggleType}
                {...commonProps}
              >
                {filterValue => {
                  const valueName =
                    filterValue.valueName?.toLowerCase() === 'in stock'
                      ? 'In Stock at My Store'
                      : filterValue.valueName;

                  return filteredFilter.categoryName === 'Ratings' ? (
                    <>
                      <StarRating
                        hideRating
                        rating={Number(filterValue.valueName?.charAt(0))}
                        styleName="stars"
                      />
                      {`${Number(filterValue.valueName?.charAt(0))} star`} (
                      {filterValue.count})
                    </>
                  ) : (
                    <>
                      {valueName}{' '}
                      {filteredFilter.categoryCode !==
                        FILTER_CATEGORY_CODES.BACKORDERED_AND_DISCONTINUED_FILTERS &&
                        `(${filterValue.count})`}
                      {filterValue.plpToolTip && (
                        <Tooltip
                          content={filterValue.plpToolTip}
                          styleName="tooltip"
                        />
                      )}
                    </>
                  );
                }}
              </OptionCategory>
            </LazyLoad>
          );
        }

        return category;
      })}
    </>
  );
}

ProductListFilter.propTypes = {
  accessoryCategory: PropTypes.string,
  defaultMaxPrice: PropTypes.number,
  defaultMinPrice: PropTypes.number,
  filters: ProductListFiltersProp,
  highestPriceInResults: PropTypes.number,
  isAccessorySearch: PropTypes.bool,
  isChangeStoreDisabled: PropTypes.bool,
  isLoadingPLP: PropTypes.bool,
  isPrebuiltPackageSearch: PropTypes.bool,
  isTextSearch: PropTypes.bool,
  lowestPriceInResults: PropTypes.number,
  onSelectFilter: PropTypes.func,
  onSliderValueChange: PropTypes.func,
  query: PropTypes.string,
  selectedFilters: PropTypes.array,
  shouldApplySliderOnChange: PropTypes.bool,
  toggleType: PropTypes.string
};

export default ProductListFilter;
